/**
 * collection-filter.js
 * Upload to: /assets/
 *
 * Features:
 *  - Client-side instant filtering (no page reload)
 *  - URL query string sync (shareable / browser back support)
 *  - Active filter pills with individual remove
 *  - Price range radio filter
 *  - Product type tag checkboxes + scent variant-option checkboxes
 *  - Sort order
 *  - Mobile sidebar open/close
 *  - Add to cart via AJAX (Shopify Cart API)
 *
 * Scent note: data-scents on each card only contains scents whose variant
 * is in stock (built in Liquid). If a scent in the URL (?scent=bel-aire)
 * has no checkbox because it is out of stock everywhere, it is kept as an
 * "extra" scent filter so the grid correctly shows no matching products
 * instead of showing everything.
 *
 * FIX: restoreFromURL() now matches checkbox values case-insensitively, so
 * links like ?type=type_diffuser+oils work even if the checkbox value is
 * "type_Diffuser Oils". Previously nothing matched, filters were empty and
 * syncURL() reset the address bar to the plain collection URL.
 */

(function () {
  'use strict';

  /* ---- DOM refs ---- */
  const form        = document.getElementById('cf-filter-form');
  const grid        = document.getElementById('cf-grid');
  const pillsWrap   = document.getElementById('cf-pills');
  const countEl     = document.getElementById('cf-count');
  const noResults   = document.getElementById('cf-no-results');
  const clearBtn    = document.getElementById('cf-clear-btn');
  const applyBtn    = document.getElementById('cf-apply-btn');
  const sortSel     = document.getElementById('cf-sort-select');
  const sidebar     = document.getElementById('cf-sidebar');
  const overlay     = document.getElementById('cf-overlay');
  const mobileToggle = document.getElementById('cf-mobile-toggle');
  const sidebarClose = document.getElementById('cf-sidebar-close');
  const filterBadge = document.getElementById('cf-filter-badge');

  if (!form || !grid) return; // section not on page

  /* ---- Collect all product cards once ---- */
  let allCards = Array.from(grid.querySelectorAll('.cf-card'));

  /* Scents requested via URL that have no checkbox (e.g. out of stock everywhere) */
  let extraScents = [];

  /* ====================================================
     COLLECTION SCOPE
  ==================================================== */
  const currentCollection = (grid.dataset.collection || 'all').toLowerCase().trim();

  /* ====================================================
     READING FILTERS FROM THE FORM
  ==================================================== */
  function getFilters() {
    const types  = Array.from(form.querySelectorAll('input[name="type"]:checked')).map(i => ({ raw: i.value, lc: i.value.toLowerCase() }));
    const scents = Array.from(form.querySelectorAll('input[name="scent"]:checked')).map(i => ({ raw: i.value, lc: i.value.toLowerCase() }));
    const offers = Array.from(form.querySelectorAll('input[name="offers"]:checked')).map(i => ({ raw: i.value, lc: i.value.toLowerCase() }));
    const priceEl = form.querySelector('input[name="price_range"]:checked');
    const price  = priceEl ? priceEl.value : '';

    /* Add scents from the URL that have no checkbox (out of stock) */
    extraScents.forEach(s => {
      if (!scents.some(x => x.lc === s.toLowerCase())) {
        scents.push({ raw: s, lc: s.toLowerCase() });
      }
    });

    return { types, scents, offers, price };
  }

  /* ====================================================
     PRICE MATCHING
  ==================================================== */
  function priceMatches(cents, range) {
    if (!range) return true;
    const dollars = parseFloat(cents);
    if (isNaN(dollars)) return true;
    if (range === '0-10')  return dollars < 10;
    if (range === '10-20') return dollars >= 10  && dollars <= 20;
    if (range === '20-30') return dollars > 20   && dollars <= 30;
    if (range === '30-50') return dollars > 30   && dollars <= 50;
    if (range === '50+')   return dollars > 50;
    return true;
  }

  /* ====================================================
     APPLY FILTERS + SORT
  ==================================================== */
  function applyFilters() {
    const { types, scents, offers, price } = getFilters();
    const sort = sortSel ? sortSel.value : 'manual';

    let visible = [];
    let hidden  = [];

    allCards.forEach(card => {
      const cardTags    = (card.dataset.tags  || '').split(',').map(t => t.trim().toLowerCase());
      /* data-scents only holds IN-STOCK scents (see Liquid) */
      const cardScents  = (card.dataset.scents || '').split('|').map(s => s.trim().toLowerCase()).filter(Boolean);
      const cardPrice   = card.dataset.price || '0';
      const cardOnSale  = card.dataset.onSale === 'true';
      const cardHoliday = cardTags.includes('holiday-shop');

      const cardCollections = (card.dataset.collections || 'all').toLowerCase().split('|').map(c => c.trim());
      const userPickedTypes = types.length > 0;
      const collectionOk = currentCollection === 'all'
        || userPickedTypes               // type filter overrides collection scope
        || cardCollections.includes(currentCollection);

      const typeOk   = types.length  === 0 || types.some(t => cardTags.includes(t.lc));
      const scentOk  = scents.length === 0 || scents.some(s => cardScents.includes(s.lc));
      const priceOk  = priceMatches(cardPrice, price);
      const offersOk = offers.length === 0 || offers.every(o => {
        if (o.lc === 'on-sale')      return cardOnSale;
        if (o.lc === 'holiday-shop') return cardHoliday;
        return false;
      });

      if (collectionOk && typeOk && scentOk && priceOk && offersOk) {
        visible.push(card);
      } else {
        hidden.push(card);
        card.setAttribute('data-hidden', 'true');
      }
    });

    visible = sortCards(visible, sort);

    [...visible, ...hidden].forEach(c => grid.appendChild(c));
    visible.forEach(c => c.removeAttribute('data-hidden'));

    const count = visible.length;
    if (countEl) countEl.textContent = count + ' product' + (count !== 1 ? 's' : '');

    if (noResults) noResults.hidden = count > 0;
    grid.hidden = count === 0;

    renderPills(types, scents, offers, price);
    updateBadge(types, scents, offers, price);
    syncURL(types, scents, offers, price, sort);
  }

  /* ====================================================
     SORT HELPERS
  ==================================================== */
  function sortCards(cards, sort) {
    if (sort === 'price-ascending') {
      return cards.slice().sort((a, b) => parseFloat(a.dataset.price) - parseFloat(b.dataset.price));
    }
    if (sort === 'price-descending') {
      return cards.slice().sort((a, b) => parseFloat(b.dataset.price) - parseFloat(a.dataset.price));
    }
    if (sort === 'title-ascending') {
      return cards.slice().sort((a, b) => {
        const na = a.querySelector('.cf-card__name')?.textContent.trim() || '';
        const nb = b.querySelector('.cf-card__name')?.textContent.trim() || '';
        return na.localeCompare(nb);
      });
    }
    if (sort === 'title-descending') {
      return cards.slice().sort((a, b) => {
        const na = a.querySelector('.cf-card__name')?.textContent.trim() || '';
        const nb = b.querySelector('.cf-card__name')?.textContent.trim() || '';
        return nb.localeCompare(na);
      });
    }
    if (sort === 'created-descending') {
      return cards;
    }
    return cards; // manual / featured = Liquid order
  }

  /* ====================================================
     FILTER PILLS
  ==================================================== */
  const PRICE_LABELS = {
    '0-10':  'Under $10',
    '10-20': '$10 – $20',
    '20-30': '$20 – $30',
    '30-50': '$30 – $50',
    '50+':   '$50 & above',
  };

  const OFFER_LABELS = {
    'on-sale':     'On Sale',
    'holiday-shop': 'Holiday Shop',
  };

  function renderPills(types, scents, offers, price) {
    if (!pillsWrap) return;

    const html = [];

    types.forEach(t => {
      const label = formatTag(t.lc, 'type_');
      html.push(`<button type="button" class="cf-pill" data-filter-name="type" data-filter-value="${t.raw}" aria-label="Remove filter: ${label}">${label} <span class="cf-pill__x" aria-hidden="true">&#x2715;</span></button>`);
    });

    scents.forEach(s => {
      const label = formatTag(s.lc, '');
      html.push(`<button type="button" class="cf-pill" data-filter-name="scent" data-filter-value="${s.raw}" aria-label="Remove filter: ${label}">${label} <span class="cf-pill__x" aria-hidden="true">&#x2715;</span></button>`);
    });

    offers.forEach(o => {
      const label = OFFER_LABELS[o.lc] || o.lc;
      html.push(`<button type="button" class="cf-pill" data-filter-name="offers" data-filter-value="${o.raw}" aria-label="Remove filter: ${label}">${label} <span class="cf-pill__x" aria-hidden="true">&#x2715;</span></button>`);
    });

    if (price) {
      const label = PRICE_LABELS[price] || price;
      html.push(`<button type="button" class="cf-pill" data-filter-name="price_range" data-filter-value="${price}" aria-label="Remove filter: ${label}">${label} <span class="cf-pill__x" aria-hidden="true">&#x2715;</span></button>`);
    }

    pillsWrap.innerHTML = html.join('');
  }

  /* Delegated click handler for pills */
  if (pillsWrap) {
    pillsWrap.addEventListener('click', (e) => {
      const pill = e.target.closest('.cf-pill');
      if (!pill) return;

      const name  = pill.dataset.filterName;
      const value = pill.dataset.filterValue;

      if (name === 'price_range') {
        const allPrice = form.querySelector('input[name="price_range"][value=""]');
        if (allPrice) allPrice.checked = true;
      } else {
        const input = findInput(name, value);
        if (input) input.checked = false;

        /* Scent with no checkbox (out of stock): remove it from the extras list */
        if (name === 'scent') {
          extraScents = extraScents.filter(s => s.toLowerCase() !== value.toLowerCase());
        }
      }

      /* If no type checkbox is left ticked, switch "View all" back on */
      if (name === 'type') {
        const viewAll = form.querySelector('.cf-view-all-radio');
        if (viewAll) viewAll.checked = !form.querySelector('.cf-type-checkbox:checked');
      }

      applyFilters();
    });
  }

  function formatTag(raw, prefix) {
    return raw.replace(prefix, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  /* ====================================================
     MOBILE BADGE
  ==================================================== */
  function updateBadge(types, scents, offers, price) {
    if (!filterBadge) return;
    const total = types.length + scents.length + offers.length + (price ? 1 : 0);
    filterBadge.textContent = total;
    filterBadge.hidden = total === 0;
  }

  /* ====================================================
     URL SYNC (shareable links)
  ==================================================== */
  function syncURL(types, scents, offers, price, sort) {
    const params = new URLSearchParams();
    types.forEach(t  => params.append('type',  t.lc));
    scents.forEach(s => params.append('scent', s.lc));
    offers.forEach(o => params.append('offers', o.lc));
    if (price)               params.set('price', price);
    if (sort && sort !== 'manual') params.set('sort', sort);

    const qs = params.toString();
    const newURL = window.location.pathname + (qs ? '?' + qs : '');
    history.replaceState(null, '', newURL);
  }

  /* Case-insensitive checkbox/radio lookup.
     Lets ?type=type_diffuser+oils match value="type_Diffuser Oils". */
  function findInput(name, value) {
    const v = String(value).trim().toLowerCase();
    return Array.from(form.querySelectorAll(`input[name="${name}"]`))
      .find(i => i.value.trim().toLowerCase() === v) || null;
  }

  /* Restore filters from URL on page load */
  function restoreFromURL() {
    const params = new URLSearchParams(window.location.search);

    params.getAll('type').forEach(t => {
      const input = findInput('type', t);
      if (input) input.checked = true;
    });

    params.getAll('scent').forEach(s => {
      const input = findInput('scent', s);
      if (input) {
        input.checked = true;
      } else if (s && !extraScents.some(x => x.toLowerCase() === s.toLowerCase())) {
        /* No checkbox => scent is out of stock everywhere. Keep it as a
           filter so no products match, rather than ignoring it. */
        extraScents.push(s);
      }
    });

    params.getAll('offers').forEach(o => {
      const input = findInput('offers', o);
      if (input) input.checked = true;
    });

    const price = params.get('price');
    if (price) {
      const input = findInput('price_range', price);
      if (input) input.checked = true;
    }

    const sort = params.get('sort');
    if (sort && sortSel) sortSel.value = sort;

    /* Keep the "View all" radio in sync with the restored type filters */
    const viewAll = form.querySelector('.cf-view-all-radio');
    if (viewAll) viewAll.checked = !form.querySelector('.cf-type-checkbox:checked');
  }

  /* ====================================================
     CLEAR ALL
  ==================================================== */
  function clearAll() {
    form.querySelectorAll('input[type="checkbox"]').forEach(i => i.checked = false);
    extraScents = [];
    const allPrice = form.querySelector('input[name="price_range"][value=""]');
    if (allPrice) allPrice.checked = true;
    const viewAll = form.querySelector('.cf-view-all-radio');
    if (viewAll) viewAll.checked = true;
    if (sortSel) sortSel.value = 'manual';
    applyFilters();
  }

  /* ====================================================
     COLLAPSIBLE FILTER SECTIONS
  ==================================================== */
  document.querySelectorAll('.cf-filter-block__toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      const body = btn.nextElementSibling;
      if (body) body.classList.toggle('cf-collapsed', expanded);
    });
  });

  /* ====================================================
     MOBILE SIDEBAR OPEN / CLOSE
  ==================================================== */
  function openSidebar() {
    sidebar.classList.add('cf-open');
    overlay.classList.add('cf-visible');
    document.body.style.overflow = 'hidden';
    sidebar.focus();
  }

  function closeSidebar() {
    sidebar.classList.remove('cf-open');
    overlay.classList.remove('cf-visible');
    document.body.style.overflow = '';
    if (mobileToggle) mobileToggle.focus();
  }

  if (mobileToggle) mobileToggle.addEventListener('click', openSidebar);
  if (sidebarClose) sidebarClose.addEventListener('click', closeSidebar);
  if (overlay)      overlay.addEventListener('click', closeSidebar);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && sidebar && sidebar.classList.contains('cf-open')) {
      closeSidebar();
    }
  });

  /* ====================================================
     ADD TO CART (Shopify AJAX Cart API)
  ==================================================== */
  grid.addEventListener('click', async (e) => {
    const atcBtn = e.target.closest('.cf-card__atc');
    if (!atcBtn) return;

    const variantId = atcBtn.dataset.variantId;
    if (!variantId) return;

    const originalText = atcBtn.textContent.trim();
    atcBtn.disabled = true;
    atcBtn.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
      });

      if (!response.ok) throw new Error('Cart error');

      atcBtn.textContent = 'Added!';
      atcBtn.style.background = '#6b8f6e';

      updateCartCount();

      setTimeout(() => {
        atcBtn.textContent = originalText;
        atcBtn.style.background = '';
        atcBtn.disabled = false;
      }, 2000);

    } catch (err) {
      atcBtn.textContent = 'Error — retry';
      atcBtn.style.background = '#b94a48';
      setTimeout(() => {
        atcBtn.textContent = originalText;
        atcBtn.style.background = '';
        atcBtn.disabled = false;
      }, 2500);
    }
  });

  async function updateCartCount() {
    try {
      const res  = await fetch('/cart.js');
      const data = await res.json();
      const count = data.item_count;
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = count;
        el.hidden = count === 0;
      });
    } catch (_) { /* silent */ }
  }

  /* ====================================================
     EVENT LISTENERS
  ==================================================== */
  if (clearBtn) clearBtn.addEventListener('click', clearAll);

  if (applyBtn) applyBtn.addEventListener('click', () => {
    applyFilters();
    closeSidebar();
  });

  form.addEventListener('change', (e) => {
    const el = e.target;

    if (el.classList.contains('cf-view-all-radio')) {
      form.querySelectorAll('.cf-type-checkbox').forEach(i => i.checked = false);
    }

    if (el.classList.contains('cf-type-checkbox') && el.checked) {
      const viewAll = form.querySelector('.cf-view-all-radio');
      if (viewAll) viewAll.checked = false;
    }

    if (el.classList.contains('cf-type-checkbox') && !el.checked) {
      const anyChecked = Array.from(form.querySelectorAll('.cf-type-checkbox')).some(i => i.checked);
      if (!anyChecked) {
        const viewAll = form.querySelector('.cf-view-all-radio');
        if (viewAll) viewAll.checked = true;
      }
    }

    applyFilters();
  });

  if (sortSel) sortSel.addEventListener('change', applyFilters);

  window.CFFilter = { clearAll };

  /* ====================================================
     INIT
  ==================================================== */
  restoreFromURL();
  applyFilters();

})();